import type React from 'react';
import { createContext, useContext, useState, useRef, useCallback } from 'react';

import ConfirmModal from './ConfirmModal';

type ConfirmOptions = {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  confirmType?: 'primary' | 'danger';
  confirmLabel?: string;
};

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [state, setState] = useState<{ open: boolean; options: ConfirmOptions }>({
    open: false,
    options: { title: '', children: null },
  });
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    setState({ open: true, options });
    return new Promise((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const handle = useCallback((result: boolean) => {
    setState((prev) => ({ ...prev, open: false }));
    resolveRef.current?.(result);
    resolveRef.current = null;
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmModal
        open={state.open}
        handleClose={() => handle(false)}
        confirmHandler={() => handle(true)}
        title={state.options.title}
        icon={state.options.icon}
        confirmType={state.options.confirmType ?? 'primary'}
        confirmLabel={state.options.confirmLabel}
      >
        {state.options.children}
      </ConfirmModal>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const confirm = useContext(ConfirmContext);
  if (!confirm) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return confirm;
}
